"use client";

import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import type { ChatMessage } from "../MessageBubble";
import { mergeMessagePage, messageCursor } from "@/lib/chat/chatMessages";
import type { MessageThread } from "./useMessageThread";
import type { ChatProcess } from "@/lib/chat/chatProcessTypes";

const POLL_MS = 4000;

/**
 * Keeps the thread current with the server: the first page on mount, then
 * cursor-based deltas every POLL_MS while the thread is visible and the tab
 * is in the foreground, plus older pages on request. Sending pauses the
 * poll, since a response that began before an optimistic send must never
 * replace it.
 *
 * Both reads stay on raw `fetch` rather than requestJson: a refused poll is
 * dropped and retried next tick, and the history read handles its own
 * refusal wording.
 *
 * While this tab is itself sending, the poll still runs for the long jobs
 * (`processes`) so Smithers' working steps keep moving, but leaves messages and cursors
 * alone until the send has finished.
 */
export function useMessagePoll({
  workerId,
  businessId,
  visible,
  scrollerRef,
  thread,
}: {
  /** null = Smithers' global cross-worker thread. */
  workerId: string | null;
  businessId: string;
  visible: boolean;
  /** The scrolling message list, whose place is kept when older pages land above it. */
  scrollerRef: RefObject<HTMLDivElement | null>;
  thread: MessageThread;
}) {
  const { mountedRef, sendingRef, stickToBottomRef, msgsRef, commitMessages } = thread;
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  /** Smithers is still on a turn this page did not send, e.g. one from before a reload. */
  const [working, setWorking] = useState(false);
  /** The long jobs under way for the business (plan builds, dry runs), drawn as Smithers' working steps. */
  const [processes, setProcesses] = useState<ChatProcess[]>([]);
  /** When the running turn began (server time), or null. */
  const [turnStartedAt, setTurnStartedAt] = useState<number | null>(null);
  /** The server clock when the processes were read, paired with the local clock, so elapsed times do not drift with the reader's clock. */
  const [clock, setClock] = useState<{ serverNow: number; localNow: number } | null>(null);
  const syncRef = useRef<AbortController | null>(null);
  const olderRef = useRef<AbortController | null>(null);
  const newestCursorRef = useRef<string | null>(null);
  const oldestCursorRef = useRef<string | null>(null);
  const syncedRef = useRef(false);
  // Automatic arrivals retain a bounded tail; explicit history reads expand it.
  const historyBudgetRef = useRef(100);
  /** The scroller's geometry just before an older page was prepended, for useStickyScroll to restore. */
  const scrollRestoreRef = useRef<{ height: number; top: number } | null>(null);

  useEffect(() => {
    return () => {
      syncRef.current?.abort();
      olderRef.current?.abort();
    };
  }, []);

  const messageUrl = useCallback((cursor?: { afterCursor?: string; beforeCursor?: string }, limit = 50) => {
    const params = new URLSearchParams({ limit: String(limit), ...(!workerId ? { businessId } : {}), ...cursor });
    return `${workerId ? `/api/workers/${workerId}/messages` : "/api/manager/messages"}?${params}`;
  }, [workerId, businessId]);

  const refreshMessages = useCallback(async (force = false) => {
    if (!mountedRef.current || (!force && (!visible || document.hidden))) return;
    if ((syncRef.current || olderRef.current) && !force) return;
    // A send in progress: only the jobs' progress is read, never the messages (see above).
    const processesOnly = !force && sendingRef.current;
    if (processesOnly && workerId) return;
    syncRef.current?.abort();
    const controller = new AbortController();
    syncRef.current = controller;
    try {
      // Catch up in bounded batches. A very large backlog resumes next tick.
      for (let batch = 0; batch < 4; batch++) {
        const after = newestCursorRef.current;
        // The processes-only read asks for the smallest page: its messages are discarded.
        const res = await fetch(messageUrl(after ? { afterCursor: after } : undefined, processesOnly ? 1 : 50), { signal: controller.signal });
        if (!res.ok) return;
        const data = await res.json();
        if (!mountedRef.current || controller.signal.aborted || syncRef.current !== controller) return;
        if (!workerId && data.businessId !== businessId) return;
        const incoming: ChatMessage[] = data.messages ?? [];
        if (!workerId) {
          setWorking(data.working === true);
          setProcesses(Array.isArray(data.processes) ? data.processes : []);
          setTurnStartedAt(typeof data.turnStartedAt === "number" ? data.turnStartedAt : null);
          if (typeof data.now === "number") setClock({ serverNow: data.now, localNow: Date.now() });
        }
        if (processesOnly) return;
        if (!syncedRef.current) {
          setHasOlder(Boolean(data.hasOlder));
          oldestCursorRef.current = data.oldestCursor ?? (incoming[0] ? messageCursor(incoming[0]) : null);
          syncedRef.current = true;
        }
        newestCursorRef.current = data.newestCursor ?? (incoming.length ? messageCursor(incoming[incoming.length - 1]) : after);
        let next = mergeMessagePage(msgsRef.current, incoming, force || !sendingRef.current);
        if (stickToBottomRef.current && next.length > historyBudgetRef.current) {
          next = next.slice(-historyBudgetRef.current);
          oldestCursorRef.current = messageCursor(next[0]);
          setHasOlder(true);
        } else if (!oldestCursorRef.current && next[0]) {
          oldestCursorRef.current = messageCursor(next[0]);
        }
        commitMessages(next);
        if (!data.hasMore || !incoming.length) break;
      }
    } catch {
      // Transient connectivity failures are retried on the next visible poll.
    } finally {
      if (syncRef.current === controller) syncRef.current = null;
    }
  }, [visible, messageUrl, workerId, businessId, commitMessages, mountedRef, sendingRef, msgsRef, stickToBottomRef]);

  async function loadOlder() {
    const before = oldestCursorRef.current;
    if (!before || olderRef.current || sendingRef.current) return;
    // Keep the older boundary fixed until this page has merged. A live-tail
    // trim during the read could otherwise leave a permanent gap between them.
    syncRef.current?.abort();
    syncRef.current = null;
    const controller = new AbortController();
    olderRef.current = controller;
    setLoadingOlder(true);
    setHistoryError(null);
    try {
      const res = await fetch(messageUrl({ beforeCursor: before }), { signal: controller.signal });
      if (!res.ok) throw new Error("Could not load older messages. Please try again.");
      const data = await res.json();
      if (!mountedRef.current || controller.signal.aborted) return;
      if (!workerId && data.businessId !== businessId) throw new Error("Business changed. Reopen Smithers before continuing.");
      const incoming: ChatMessage[] = data.messages ?? [];
      if (incoming.length) {
        const scroller = scrollerRef.current;
        if (scroller) scrollRestoreRef.current = { height: scroller.scrollHeight, top: scroller.scrollTop };
        stickToBottomRef.current = false;
        historyBudgetRef.current += incoming.length;
        oldestCursorRef.current = data.oldestCursor ?? messageCursor(incoming[0]);
        commitMessages(mergeMessagePage(msgsRef.current, incoming), false);
      }
      setHasOlder(Boolean(data.hasOlder));
    } catch (error) {
      if (mountedRef.current && !controller.signal.aborted) setHistoryError(error instanceof Error ? error.message : "Could not load older messages.");
    } finally {
      if (olderRef.current === controller) {
        olderRef.current = null;
        if (mountedRef.current) setLoadingOlder(false);
      }
    }
  }

  // Hidden drawers retain their draft and streaming reply, but perform no polling.
  useEffect(() => {
    if (!visible) return;
    const t0 = setTimeout(() => void refreshMessages(), 0);
    const id = setInterval(() => void refreshMessages(), POLL_MS);
    function onRefresh(e: Event) {
      const detail = (e as CustomEvent<{ workerId?: string }>).detail;
      if (workerId && detail?.workerId && detail.workerId !== workerId) return;
      void refreshMessages();
    }
    function onVisibility() {
      if (document.hidden) syncRef.current?.abort();
      else void refreshMessages();
    }
    window.addEventListener("caw:messages-refresh", onRefresh);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearTimeout(t0);
      clearInterval(id);
      syncRef.current?.abort();
      syncRef.current = null;
      olderRef.current?.abort();
      window.removeEventListener("caw:messages-refresh", onRefresh);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refreshMessages, workerId, visible]);

  return {
    hasOlder,
    loadingOlder,
    setLoadingOlder,
    historyError,
    working,
    setWorking,
    processes,
    turnStartedAt,
    clock,
    refreshMessages,
    loadOlder,
    syncRef,
    olderRef,
    scrollRestoreRef,
  };
}

export type MessageHistory = ReturnType<typeof useMessagePoll>;
